import { useState } from 'react';

import { Icon } from '~/modules/elements/akui/icon';
import SimplifiedMic from '~/modules/game-engine/input/simplified-mic';
import { transportErrorReason } from '~/modules/remote-mic/network/client/network-client';
import usePermissions from '~/routes/remote-mic/hooks/use-permissions';
import useRemoteMicId from '~/routes/remote-mic/hooks/use-remote-mic-id';
import ManageGameSheet from '~/routes/remote-mic/panels/remote-settings/manage-game-sheet';
import MicrophoneSettingsSheet from '~/routes/remote-mic/panels/remote-settings/microphone-settings-sheet';
import { ConnectionStatuses } from '~/routes/remote-mic/remote-mic';

interface Props {
  roomId: string | null;
  connectionStatus: ConnectionStatuses;
  connectionError?: transportErrorReason;
  isKeepAwakeOn: boolean;
  setIsKeepAwakeOn: (keepAwake: boolean) => void;
  monitoringStarted: boolean;
  setMonitoringStarted: (micMonitoring: boolean) => void;
  onMicMuted: () => void;
}

// Was two buttons and four bare lines of text. The three things about this phone are now a card of
// rows that read as a list, and the two panels that open onto the game are pinned to the bottom
// where a thumb reaches them - and are sheets rather than modals, like every other panel here.
function RemoteSettings({ setIsKeepAwakeOn, monitoringStarted, isKeepAwakeOn, onMicMuted }: Props) {
  const permissions = usePermissions();
  const [sheet, setSheet] = useState<'microphone' | 'manage' | null>(null);
  const micId = useRemoteMicId()?.slice(-4).toUpperCase() ?? '----';

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto overscroll-contain px-5 py-4.5">
      <span className="text-[1.375rem] font-bold">This phone</span>
      <div className="flex flex-col overflow-hidden rounded-[1.125rem] bg-black/55 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.14)] [&>*:not(:last-child)]:border-b [&>*:not(:last-child)]:border-white/10">
        <button
          type="button"
          onClick={() => {
            if (monitoringStarted) {
              SimplifiedMic.stopMonitoring();
              onMicMuted();
            } else {
              SimplifiedMic.startMonitoring();
            }
          }}
          data-test="monitoring-toggle"
          className="flex h-15 items-center gap-3 border-none bg-transparent px-4.5 text-left text-white transition-colors duration-150 active:bg-white/6">
          <span className="min-w-0 flex-1 text-[1.0625rem]">Microphone</span>
          <strong
            data-test="monitoring-state"
            className={`shrink-0 text-[1.0625rem] transition-colors duration-200 ${monitoringStarted ? 'text-active' : 'text-white/50'}`}>
            {monitoringStarted ? 'ON' : 'OFF'}
          </strong>
        </button>
        <button
          type="button"
          onClick={() => setIsKeepAwakeOn(!isKeepAwakeOn)}
          data-test="wakelock-toggle"
          className="flex h-15 items-center gap-3 border-none bg-transparent px-4.5 text-left text-white transition-colors duration-150 active:bg-white/6">
          <span className="min-w-0 flex-1 text-[1.0625rem]">Keep screen awake</span>
          <strong
            data-test="wakelock-state"
            className={`shrink-0 text-[1.0625rem] transition-colors duration-200 ${isKeepAwakeOn ? 'text-active' : 'text-white/50'}`}>
            {isKeepAwakeOn ? 'ON' : 'OFF'}
          </strong>
        </button>
        <div className="flex h-15 items-center gap-3 px-4.5">
          <span className="min-w-0 flex-1 text-[1.0625rem]">Remote mic ID</span>
          <span data-test="remote-mic-id" className="shrink-0 font-mono text-[1rem] text-white/55">
            {micId}
          </span>
        </div>
      </div>

      <div className="mt-auto flex flex-col gap-2.5">
        <PanelRow
          icon="ic:baseline-settings-voice"
          label="Microphone settings"
          test="microphone-settings"
          onClick={() => setSheet('microphone')}
        />
        {/* Only a phone that can drive the game can change the game's own settings. */}
        {permissions === 'write' && (
          <PanelRow
            icon="ic:round-people-alt"
            label="Manage the game"
            test="manage-game"
            onClick={() => setSheet('manage')}
          />
        )}
      </div>

      <MicrophoneSettingsSheet open={sheet === 'microphone'} onClose={() => setSheet(null)} />
      <ManageGameSheet open={sheet === 'manage'} onClose={() => setSheet(null)} />
    </div>
  );
}

function PanelRow({
  icon,
  label,
  test,
  onClick,
}: {
  icon: Parameters<typeof Icon>[0]['icon'];
  label: string;
  test: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-test={test}
      className="flex h-16 cursor-pointer items-center gap-3 rounded-[1.125rem] border-none bg-black/50 px-4.5 text-left text-white shadow-[inset_0_0_0_1px_rgba(255,255,255,0.14)] transition-transform duration-100 active:scale-[0.98]">
      <Icon icon={icon} size={6} className="text-active shrink-0" />
      <span className="min-w-0 flex-1 truncate text-[1.0625rem]">{label}</span>
      <Icon icon="ic:baseline-arrow-forward" size={5.5} className="shrink-0 opacity-70" />
    </button>
  );
}

export default RemoteSettings;
